/**
 * Room epochs, the SPA half (AB#124, Backup D 1/3; the backend half is backend/domain/room-epoch.ts
 * — see docs/spec/30-coediting.md "Room epochs").
 *
 * Every room carries an epoch the server bumps when it REPLACES the room's state (a restore, a bid
 * delete). The server tells each connection the epoch of what it syncs (a stateless message), and
 * this tab presents it back on every reconnect (the provider parameter `epoch`). A tab whose Y.Doc
 * predates a replacement is refused with `room_restored` — on the handshake, or as the close reason
 * when the server evicts the live room — and must drop that Y.Doc: reopen the part on a NEW one,
 * never resync the old content into the restored room (owner decision, 2026-10-01).
 *
 * A fresh Y.Doc presents no epoch: the parameters start without one.
 */

/** The refusal / close reason of a room whose state was replaced server-side. */
export const ROOM_RESTORED = 'room_restored'
/** The provider parameter carrying the epoch this tab's Y.Doc last synced. */
export const ROOM_EPOCH_PARAM = 'epoch'
/** `type` of the stateless message announcing the room's epoch. */
export const ROOM_EPOCH_MESSAGE = 'niobe.room-epoch'

/** A room refusal or close reason meaning "this room was replaced: reopen it fresh". */
export function isRoomRestored(reason: string | null | undefined): boolean {
  return typeof reason === 'string' && reason.startsWith(ROOM_RESTORED)
}

/** The epoch announced by a stateless payload, or null when it is any other message. */
export function readEpochMessage(payload: string): number | null {
  let v: unknown
  try {
    v = JSON.parse(payload)
  } catch {
    return null
  }
  if (typeof v !== 'object' || v === null) return null
  const { type, epoch } = v as { type?: unknown; epoch?: unknown }
  if (type !== ROOM_EPOCH_MESSAGE || typeof epoch !== 'number' || !Number.isInteger(epoch) || epoch < 0) return null
  return epoch
}

/**
 * Record an announced epoch into `parameters` — which must be the very object handed to the
 * provider as `parameters`: @hocuspocus/provider 2.x keeps that reference and rebuilds the WS URL
 * from it on EVERY (re)connect (HocuspocusProviderWebsocket#url), so the next reconnect presents it.
 */
export function noteRoomEpoch(parameters: Record<string, unknown>, payload: string): void {
  const epoch = readEpochMessage(payload)
  if (epoch !== null) parameters[ROOM_EPOCH_PARAM] = String(epoch)
}
